import { z } from 'zod'
import type { RefinementCtx } from 'zod'
import { codePointLength, trimForValidate } from '@/utils/stringLength'
import { trimmedString } from './stringLength'

/** 单行/多行文本行业标准上限 */
export const TEXT_LIMITS = {
  singleLine: 255,
  title: 200,
  shortTitle: 80,
  textarea: 2000,
  remark: 10000,
  /** 人员备注（库/接口 1000） */
  bizRemark: 1000,
  url: 2048,
  auditReason: 500,
  nameShort: 64,
  code: 64,
  /** 护照号录入 */
  passportNo: 20,
  /** 身份证件地址 */
  idCardAddress: 500,
  /** 国内/企业详细地址 */
  detailAddress: 500,
  issueAuthority: 200,
  documentValidity: 50,
  registeredCapital: 100,
  departUnit: 200,
  emergencyContactName: 100,
} as const

export function requiredText(label: string, max: number = TEXT_LIMITS.singleLine, min = 1) {
  return trimmedString({ label, max, min, required: true })
}

export function optionalText(max: number = TEXT_LIMITS.singleLine) {
  return z
    .string()
    .superRefine((raw, ctx) => {
      const v = trimForValidate(raw)
      if (!v) return
      if (codePointLength(v) > max) {
        ctx.addIssue({ code: 'custom', message: `不能超过 ${max} 个字符` })
      }
    })
    .transform(trimForValidate)
}

export const requiredTitleSchema = requiredText('标题', TEXT_LIMITS.title)
export const requiredShortTitleSchema = requiredText('标题', TEXT_LIMITS.shortTitle)
export const optionalRemarkSchema = optionalText(TEXT_LIMITS.remark)

function validateUrlValue(v: string, ctx: RefinementCtx, required: boolean): void {
  if (!v) {
    if (required) {
      ctx.addIssue({ code: 'custom', message: '请输入链接地址' })
    }
    return
  }
  if (codePointLength(v) > TEXT_LIMITS.url) {
    ctx.addIssue({ code: 'custom', message: `链接不能超过 ${TEXT_LIMITS.url} 个字符` })
    return
  }
  try {
    const parsed = new URL(v.includes('://') ? v : `https://${v}`)
    const host = parsed.hostname
    if (host !== 'localhost' && !host.includes('.')) {
      ctx.addIssue({ code: 'custom', message: '请输入正确的链接地址' })
    }
  } catch {
    ctx.addIssue({ code: 'custom', message: '请输入正确的链接地址' })
  }
}

export const optionalUrlSchema = z
  .string()
  .transform(trimForValidate)
  .superRefine((v, ctx) => validateUrlValue(v, ctx, false))

export const requiredUrlSchema = z
  .string()
  .transform(trimForValidate)
  .superRefine((v, ctx) => validateUrlValue(v, ctx, true))

export const auditRejectReasonSchema = requiredText('驳回原因', TEXT_LIMITS.auditReason)

export const requiredSelectSchema = (label: string) =>
  z.preprocess(
    (v) => (v == null || v === '' ? undefined : v),
    z.any().refine((v) => v !== undefined && v !== null && v !== '', `请选择${label}`),
  )

/** 日期/时间选择（Dayjs 或字符串等非空值） */
export const requiredDateSchema = (label: string) =>
  z.any().refine((v) => v != null && v !== '', `请选择${label}`)

/** 非空字符串日期（YYYY-MM-DD 等） */
export const requiredDateStringSchema = (label: string) =>
  z
    .string()
    .transform((s) => s.trim())
    .refine((v) => v.length > 0, `请选择${label}`)

export const requiredArraySchema = (label: string, min = 1) =>
  z.preprocess(
    (v) => (Array.isArray(v) ? v : []),
    z.array(z.any()).min(min, `请至少选择一项${label}`),
  )

/** 富文本：去 HTML 后至少 1 字符。`maxPlain` 为 `null` 时不限制长度。 */
export function htmlContentSchema(label: string, maxPlain: number | null = TEXT_LIMITS.textarea) {
  return z.string().superRefine((raw, ctx) => {
    const plain = String(raw ?? '')
      .replace(/<[^>]*>/g, '')
      .replace(/\u00a0/g, ' ')
      .trim()
    if (!plain) {
      ctx.addIssue({ code: 'custom', message: `请输入${label}` })
      return
    }
    if (maxPlain != null && codePointLength(plain) > maxPlain) {
      ctx.addIssue({ code: 'custom', message: `${label}不能超过 ${maxPlain} 个字符` })
    }
  })
}

/** 非负整数排序码 */
export const sortCodeSchema = z.preprocess(
  (v) => (v === null || v === undefined || v === '' ? undefined : Number(v)),
  z
    .number({ message: '请输入排序码' })
    .int('排序码须为整数')
    .min(0, '排序码不能小于 0')
    .max(999999, '排序码过大'),
)
